"use client";

import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
} from "@ui/carousel";
import { Lightbox } from "@ui/lightbox";
import { ComponentProps, useEffect, useRef } from "react";
import type { ProductMedia } from "../types";
import ProductGalleryImage from "./ProductGalleryImage";
import ProductGalleryThumbnail from "./ProductGalleryThumbnail";
import ProductGalleryVideo from "./ProductGalleryVideo";

type ProductGalleryProps = {
  media: ProductMedia[];
} & ComponentProps<"div">;

function ProductGallerySlide({
  item,
  index,
}: {
  item: ProductMedia;
  index: number;
}) {
  const { selectedIndex } = useCarousel();
  if (Math.abs(index - selectedIndex) > 1) return null;

  if (item.type === "video") {
    return (
      <ProductGalleryVideo
        src={item.src}
        className="absolute inset-0 size-full object-cover"
      />
    );
  }

  return (
    <ProductGalleryImage
      src={item.src}
      alt={item.alt}
      index={index}
      width={1000}
      height={1000}
      className="absolute inset-0 size-full object-contain mix-blend-multiply"
    />
  );
}

export default function ProductGallery({
  media,
  className,
  children,
  ...props
}: ProductGalleryProps) {
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const desktop = window.matchMedia("(min-width: 1024px)");

    const update = () => {
      const headerHeight = document.querySelector("header")?.offsetHeight ?? 0;
      const strip = stage.parentElement?.previousElementSibling;
      const stripHeight = desktop.matches
        ? 0
        : (strip?.getBoundingClientRect().height ?? 0);
      stage.style.maxHeight = `${Math.max(window.innerHeight - headerHeight - stripHeight, 0)}px`;
    };

    update();
    desktop.addEventListener("change", update);
    window.addEventListener("resize", update);
    const header = document.querySelector("header");
    const observer = new ResizeObserver(update);
    if (header) observer.observe(header);

    return () => {
      desktop.removeEventListener("change", update);
      window.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [media.length]);

  if (!media.length) {
    return <div />;
  }

  return (
    <Carousel
      className={cn("flex items-stretch max-lg:flex-col-reverse", className)}
      {...props}
    >
      <div className="relative shrink-0 max-lg:h-16 max-lg:w-full lg:w-16">
        <nav className="absolute inset-0 flex gap-2 p-2 max-lg:overflow-x-auto lg:flex-col lg:overflow-y-auto">
          {media.map((asset, index) => (
            <ProductGalleryThumbnail
              key={asset.id}
              index={index}
              media={asset}
            />
          ))}
        </nav>
      </div>
      <div className="min-w-0 grow">
        <div
          ref={stageRef}
          className="aspect-portrait relative max-h-[calc(100svh-var(--header-height))] w-full max-lg:max-h-[calc(100svh-var(--header-height)-4rem)] lg:aspect-square"
        >
          <div className="absolute inset-0 **:data-[slot=carousel-content]:h-full">
            <CarouselContent className="h-full">
              {media.map((item, index) => (
                <CarouselItem
                  key={item.id}
                  className="relative h-full basis-full bg-gray-100"
                  data-lightbox-src={item.src}
                  data-lightbox-type={item.type}
                  data-lightbox-alt={item.alt}
                >
                  <ProductGallerySlide item={item} index={index} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselNext />
            <CarouselPrevious />
            <Lightbox />
            {children}
          </div>
        </div>
      </div>
    </Carousel>
  );
}
